import React, { useState, useEffect, useCallback } from "react";
import ReactTable from "react-table";
import Select from "react-select";
import PageTitle from "../PageTitle/page_title";
import i18next from "i18next";
import { ChecklistReportService, TimeFormattingService } from "../../services";
import { DatePickerInput } from "../DatePickerInput";
import moment from "moment-timezone";

const ChecklistComplianceReportForm = ({ availableGroupingValues, availableTagValues, onSubmit }) => {
  const DATE_RANGE_OPTIONS = [
    {value: 'day', label: i18next.t('day')},
    {value: 'week', label: i18next.t('week')}
  ];

  const ALL_GROUPING = {value: -1, label: 'All', grouping: {id: -1}};

  const [dateRange, setDateRange] = useState(DATE_RANGE_OPTIONS[0]);
  const [selectedGrouping, setSelectedGrouping] = useState(null);
  const [selectedChecklistTag, setSelectedChecklistTag] = useState(null);
  const [startDate, setStartDate] = useState(null);

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    onSubmit({ selectedGrouping: selectedGrouping.value, selectedChecklistTag: selectedChecklistTag.value, dateRange: dateRange.value, startDate });
  }, [onSubmit, selectedGrouping, selectedChecklistTag, dateRange, startDate]);

  useEffect(() => {
    setStartDate(null);
  }, [dateRange]);

  return (
    <div className="op-content-box filter-box" style={{marginBottom: 20}}>

      <form onSubmit={handleSubmit}>

        <ul className="list-inline clearfix">
          <li className="pull-left box-label"><h4 className="remove-margin-bottom">{i18next.t('filters')}</h4></li>
        </ul>

        <div className = "row">
          <div className="col-sm-3">
            <label>{i18next.t('timeframe')}</label>
            <Select
                options={DATE_RANGE_OPTIONS}
                clearable={false}
                value={dateRange}
                onChange={setDateRange}
                searchable={false}
                classNamePrefix="react-select"
                placeholder ={ i18next.t('select-a-timeframe') }
              />
          </div>

          <div className="pull-left" style={{"maxWidth": "150px", margin: "0px 0px 0px 10px"}}>
              <label>{ i18next.t('start-date') }</label>
              <DatePickerInput
                input={{value: startDate}}
                meta={{}}
                onDayChange={setStartDate}
                dayPickerProps={{
                    disabledDays: dateRange.value == 'day' ? {} : {daysOfWeek: [1,2,3,4,5,6]}
                }}
              />
          </div>

          <div className="col-sm-3">
            <label>{ i18next.t('location') }</label>
            <Select
                options={[ALL_GROUPING, ...availableGroupingValues]}
                clearable={false}
                value={selectedGrouping}
                onChange={setSelectedGrouping}
                classNamePrefix="react-select"
                placeholder={ i18next.t('select-a-location') }
              />
          </div>

          <div className="col-sm-3">
             <label>Tag</label>
             <Select
                options={availableTagValues}
                clearable={false}
                value={selectedChecklistTag}
                onChange={setSelectedChecklistTag}
                classNamePrefix="react-select"
                placeholder={"Select a Tag"}
              />
          </div>

          <div className="pull-left">
            <label style={{visibility: 'hidden'}}>{ i18next.t('run-report') }</label>
            <ul className="list-inline clearfix">
              <li className="pull-left" style={{marginLeft: '15px'}}>
                <button type="submit" className="btn btn-sm btn-default pull-right" disabled={!(selectedGrouping && startDate && selectedChecklistTag)}>{ i18next.t('form-labels.run-report') }</button>
              </li>
            </ul>
          </div>          

        </div>
      </form>
    </div>
  );
};

const ChecklistComplianceReportResults = ({ query, onError }) => {
    const [results, setResults] = useState(null);

    useEffect(() => {
        if (query) {
            setResults(null);

            const groupingId = query.selectedGrouping;
            const startDate = moment(query.startDate).utc().startOf("day").valueOf();
            const endDate = moment(startDate).add(1, query.dateRange).utc().valueOf();
            const tagId = query.selectedChecklistTag;

            ChecklistReportService.getChecklistComplianceReport(groupingId, startDate, endDate, tagId).then((response) => {
                setResults(response.data);
            }).catch((error) => {
                console.error(error);
                onError("Unable to fetch report data.");
            });
        } else {
            setResults(null);
        }
    }, [query]);

    if (query) {
        return results ? (
            <div>
                <div className="op-content-box">
                    <ul className="list-inline clearfix">
                        <li className="pull-left box-label"><h4 className="remove-margin-bottom">{i18next.t('results')}</h4></li>
                    </ul>
                    
                    <div>
                        <div className="react-table-custom">
                            <ReactTable
                                    className="-striped -highlight"
                                    data={results}
                                    showPagination={false}
                                    pageSize={results.length}
                                    defaultSorted={[{
                                        id: 'locationName',
                                        desc: false // not necessary unless set to true, but it's good to know its there.
                                    }]}
                                    columns={[{
                                        Header: 'Location',
                                        accessor: "locationName",
                                        minWidth: 100,
                                        minResizeWidth: 200,
                                        width: 200
                                    }, {
                                        Header: i18next.t('table-headers.checklist'),
                                        accessor: "checklistName",
                                        Cell: ({value, original}) => `${value} (V${original.checklistVersion})`,
                                        minWidth: 100,
                                        minResizeWidth: 100,
                                        width: 200
                                    }, {
                                        Header: 'Task',
                                        accessor: "checklistTaskName",
                                        minWidth: 100,
                                        minResizeWidth: 100,
                                        width: 300
                                    }, {
                                        Header: 'Task Start Time',
                                        accessor: "taskStartTime",
                                        Cell: ({value, original}) => <span>{TimeFormattingService.renderNormalTime(value, original.locationTimezone)}</span>,
                                        minWidth: 100,
                                        minResizeWidth: 100
                                    }, {
                                        Header: 'Task End Time',
                                        accessor: "taskEndTime",
                                        Cell: ({value, original}) => <span>{(value && value > 0) ? TimeFormattingService.renderNormalTime(value, original.locationTimezone) : ''}</span>,
                                        minWidth: 100,
                                        minResizeWidth: 100
                                    }]}
                                />
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            <div className="loader">{i18next.t('loading')}</div>
        );
    } else {
        return null;
    }
};

const ChecklistComplianceReport = () => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [query, setQuery] = useState(null);
    const [availableGroupingValues, setAvailableGroupingValues] = useState(null);
    const [availableTagValues, setAvailableTagValues] = useState(null);

    useEffect(() => {
        ChecklistReportService.getChecklistComplianceReportOptions()
            .then(({ data }) => {
                const leafGroupings = data.locations
                    .filter((grouping) => grouping.deviceGroup?.children?.length == 0)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((grouping) => ({ value: grouping.id, label: grouping.name, grouping: grouping }));

                setAvailableGroupingValues(leafGroupings);
                setAvailableTagValues(data.tags.map(tag => ({ value: tag.id, label: tag.name, tag: tag })));
            })
            .catch((error) => {
                setErrorMessage(i18next.t("pages.corrective-actions.loadGroupings-error-message"));
                console.error(error);
            });
    }, []);

    if (availableGroupingValues && availableTagValues) {
        return (
            <div>
                <div className="row">
                    <PageTitle title="Compliance Report" />
                </div>
                {errorMessage && <div>{errorMessage}</div>}
                <div className="device-detail-report col-sm-12">
                    <ChecklistComplianceReportForm availableGroupingValues={availableGroupingValues} availableTagValues={availableTagValues} onSubmit={setQuery} />
                </div>
                <div className="device-detail-report col-sm-12">{!errorMessage && query && <ChecklistComplianceReportResults query={query} onError={setErrorMessage} />}</div>
            </div>
        );
    } else {
        return <div className="loader">{i18next.t("loading")}</div>;
    }
};

export default ChecklistComplianceReport;